/**
 * Dates de l'app : conversion en fuseau local, et date éditée par les formulaires.
 *
 * ``toISODate`` / ``todayISO`` servent partout où une date est convertie en clé
 * `YYYY-MM-DD` — calendriers compris, pas seulement les formulaires.
 *
 * Les trois formulaires acceptent `?date=YYYY-MM-DD` pour permettre le rattrapage
 * (J-1, J-2, n'importe quel jour passé). Seul FormCycle lisait ce paramètre :
 * FormMental et FormSport codaient « aujourd'hui » en dur, à la relecture comme à
 * l'enregistrement. Sélectionner un jour passé dans le calendrier écrasait donc le
 * relevé du jour, sans que rien ne le signale.
 *
 * Ce hook centralise les trois besoins : quelle date on édite, comment la propager
 * d'un onglet à l'autre, et comment l'afficher quand ce n'est pas aujourd'hui.
 */
import { useSearchParams } from 'react-router-dom';

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

/**
 * Une date au format `YYYY-MM-DD`, dans le fuseau de l'utilisatrice.
 *
 * `toISOString()` renvoie de l'UTC : passé minuit en heure française (UTC+1/+2), il
 * désigne encore la veille. Deux dégâts distincts en découlaient — un formulaire rempli
 * à 00 h 30 était enregistré sur la mauvaise journée, et les calendriers mélangeaient
 * un numéro de jour local (`getDate()`) avec une clé de date UTC, si bien que le contenu
 * d'un jour s'affichait dans la case voisine.
 */
export function toISODate(date) {
  const d = date instanceof Date ? date : new Date(date);
  const mois = String(d.getMonth() + 1).padStart(2, '0');
  const jour = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mois}-${jour}`;
}

/**
 * `YYYY-MM-DD` → Date à minuit **local**, ou null si illisible.
 *
 * `new Date('2026-03-01')` lit la chaîne comme de l'UTC : à l'ouest de Greenwich elle
 * tombe la veille au soir, et l'écran affichait le jour précédent. Toute autre forme
 * (datetime ISO complet, objet Date) suit le parseur natif.
 */
export function parseISODate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  // `new Date(null)` vaut l'epoch, pas une date invalide.
  if (value == null || value === '') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date du jour, dans le fuseau de l'utilisatrice. */
export function todayISO() {
  return toISODate(new Date());
}

/** « mardi 18 août » — pour dire à l'écran quel jour est en cours d'édition. */
export function formatDateLabel(iso) {
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);
  return `${JOURS[date.getDay()]} ${d} ${MOIS[m - 1]}`;
}

export function useFormDate() {
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const today = todayISO();
  const selectedDate = dateParam || today;

  return {
    /** Date que le formulaire lit et enregistre. */
    selectedDate,
    /** Vrai si on édite un jour autre qu'aujourd'hui (rattrapage). */
    isBackfill: selectedDate !== today,
    /** Libellé lisible de cette date. */
    label: formatDateLabel(selectedDate),
    /**
     * Reporte la date sur un lien interne. Sans elle, passer de l'onglet Cycle à
     * l'onglet Mental en plein rattrapage ramenait silencieusement à aujourd'hui.
     */
    withDate: (path) => (dateParam ? `${path}?date=${encodeURIComponent(dateParam)}` : path),
  };
}
